import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Tag, X, MousePointerClick, Package, Wrench, Clock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import ImageUpload from "@/components/ImageUpload";
import GalleryUpload from "@/components/GalleryUpload";

const CURRENCIES = ["USD", "COP", "MXN", "EUR", "VES", "ARS", "CLP", "PEN"];
const DURATION_CHIPS = ["30 min", "1 hr", "2 hrs", "Por sesión", "Por proyecto"];

// Quick-start templates so a non-tech user can publish their first item in 1 click.
const TEMPLATES = [
  { type: "service", title: "Consulta médica general", description: "Consulta médica general de 30 minutos.", duration: "30 min", price: "60", currency: "USD", category: "Salud" },
  { type: "service", title: "Corte de pelo", description: "Corte, lavado y peinado.", duration: "45 min", price: "30", currency: "USD", category: "Belleza" },
  { type: "service", title: "Asesoría legal", description: "Asesoría legal por hora.", duration: "1 hr", price: "80", currency: "USD", category: "Legal" },
  { type: "product", title: "Empanadas caseras (docena)", description: "Docena de empanadas hechas al momento.", duration: "", price: "18", currency: "USD", category: "Comida" },
];

const emptyForm = {
  id: "",
  title: "",
  description: "",
  category: "",
  price: "",
  currency: "USD",
  image: "",
  gallery: [],
  type: "product",
  duration: "",
};

const formatMoney = (price, currency) => {
  if (price == null || price === "") return null;
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", maximumFractionDigits: 2 }).format(price);
  } catch {
    return `${currency || ""} ${Number(price).toFixed(2)}`;
  }
};

const ProductCatalogManager = ({ profileId = null, onChange } = {}) => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [limit, setLimit] = useState(3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null = closed, otherwise form data
  const [saving, setSaving] = useState(false);

  const params = profileId ? { profile_id: profileId } : {};

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/products/me", { params });
      setItems(data.items || []);
      setLimit(data.limit || 3);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    if (items.length >= limit) return;
    setEditing({ ...emptyForm, mode: "new" });
  };
  const openFromTemplate = (tpl) => {
    if (items.length >= limit) return;
    setEditing({ ...emptyForm, ...tpl, mode: "new" });
  };
  const openEdit = (p) => setEditing({ ...emptyForm, ...p, price: p.price == null ? "" : String(p.price), mode: "edit" });
  const closeModal = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true); setError("");
    try {
      const payload = {
        title: editing.title.trim(),
        description: (editing.description || "").trim(),
        category: (editing.category || "").trim(),
        price: editing.price === "" || editing.price == null ? null : Number(editing.price),
        currency: editing.currency || "USD",
        image: editing.image || "",
        gallery: Array.isArray(editing.gallery) ? editing.gallery : [],
        type: editing.type || "product",
        duration: (editing.duration || "").trim(),
      };
      if (editing.mode === "new") {
        await api.post("/products", payload, { params });
      } else {
        await api.put(`/products/${editing.id}`, payload);
      }
      await load();
      onChange?.();
      closeModal();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t.catalog.confirmDelete)) return;
    try {
      await api.delete(`/products/${p.id}`);
      await load();
      onChange?.();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  if (loading) {
    return <div className="text-teal-soft text-sm" data-testid="catalog-loading">…</div>;
  }

  const isFull = items.length >= limit;
  const emptySlots = Math.max(0, limit - items.length);

  return (
    <section className="mt-10 pt-8 border-t border-gray-100" data-testid="catalog-manager">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow text-orange">{t.catalog.eyebrow}</p>
          <h2 className="font-display text-3xl text-teal-deep mt-1 leading-tight">
            {t.catalog.heading}
          </h2>
          <p className="text-sm text-teal-soft mt-2 max-w-xl">
            {t.catalog.subtitle.replace("{limit}", limit)}
          </p>
        </div>
        <button
          type="button"
          onClick={openNew}
          disabled={isFull}
          className="btn-orange disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="catalog-add-btn"
        >
          <Plus size={14} /> {t.catalog.addBtn}
        </button>
      </div>

      {isFull && (
        <p className="mt-3 text-sm text-orange bg-orange/10 rounded-xl px-4 py-2 inline-block">
          {t.catalog.limitReached.replace("{limit}", limit)}
        </p>
      )}

      {error && <p className="text-red-600 text-sm mt-3" data-testid="catalog-error">{error}</p>}

      {/* Templates strip — only when the catalog is empty, to give a 1-click starting point */}
      {items.length === 0 && !isFull && (
        <div className="mt-6 bg-white rounded-2xl border border-gray-200 p-5" data-testid="catalog-templates">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg bg-orange/10 text-orange flex items-center justify-center">
              <Package size={15} />
            </span>
            <div>
              <h3 className="font-display text-teal-deep text-base leading-tight">
                {t.catalog.tplTitle || "Empieza con una plantilla"}
              </h3>
              <p className="text-xs text-teal-soft leading-tight mt-0.5">
                {t.catalog.tplSub || "Un click, edita los datos, y publica."}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {TEMPLATES.map((tpl) => (
              <button
                key={tpl.title}
                type="button"
                onClick={() => openFromTemplate(tpl)}
                className="text-left rounded-xl border border-gray-200 hover:border-orange hover:bg-orange/5 p-3 transition-all"
                data-testid={`catalog-tpl-${tpl.title}`}
              >
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-md px-1.5 py-0.5 ${tpl.type === "service" ? "bg-teal/15 text-teal" : "bg-orange/15 text-orange"}`}>
                  {tpl.type === "service" ? <Wrench size={9} /> : <Package size={9} />}
                  {tpl.type === "service" ? (t.catalog.serviceLabel || "Servicio") : (t.catalog.productLabel || "Producto")}
                </span>
                <div className="font-display text-sm text-teal-deep leading-tight mt-1.5">{tpl.title}</div>
                <div className="text-[11px] text-teal-soft mt-1 leading-tight">
                  {tpl.duration && <span className="inline-flex items-center gap-1"><Clock size={9} /> {tpl.duration} · </span>}
                  ${tpl.price} {tpl.currency}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Items grid with empty placeholders */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((p) => {
          const money = formatMoney(p.price, p.currency);
          return (
            <div
              key={p.id}
              className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col"
              data-testid={`catalog-item-${p.id}`}
            >
                <div className="aspect-square bg-cream flex items-center justify-center overflow-hidden relative">
                  {p.image ? (
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                  ) : (Array.isArray(p.gallery) && p.gallery[0] ? (
                    <img src={p.gallery[0]} alt={p.title} className="w-full h-full object-cover" />
                  ) : (
                    (p.type === "service" ? <Wrench size={32} className="text-teal-soft/40" /> : <Tag size={32} className="text-teal-soft/40" />)
                  ))}
                  <span className={`absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${p.type === "service" ? "bg-teal text-white" : "bg-orange text-white"}`} data-testid={`catalog-badge-${p.id}`}>
                    {p.type === "service" ? <Wrench size={10} /> : <Package size={10} />}
                    {p.type === "service" ? t.catalog.serviceLabel : t.catalog.productLabel}
                  </span>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-display text-lg text-teal-deep leading-tight">{p.title}</h3>
                  {p.category && (
                    <span className="text-xs text-teal-soft mt-1">{p.category}</span>
                  )}
                  {p.duration && (
                    <span className="inline-flex items-center gap-1 text-xs text-teal-soft mt-1">
                      <Clock size={11} /> {p.duration}
                    </span>
                  )}
                  {p.description && (
                    <p className="text-sm text-teal-soft mt-2 line-clamp-2">{p.description}</p>
                  )}
                  <div className="mt-3 flex items-baseline justify-between">
                    <span className="font-display text-xl text-orange">
                      {money ?? <span className="text-teal-soft text-sm italic">{t.catalog.askPrice}</span>}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-teal-soft">
                      <MousePointerClick size={12} /> {p.click_count}
                    </span>
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openEdit(p)}
                      className="text-sm text-teal hover:text-orange inline-flex items-center gap-1"
                      data-testid={`catalog-edit-${p.id}`}
                    >
                      <Pencil size={13} /> {t.catalog.editBtn}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(p)}
                      className="text-sm text-red-500 hover:text-red-700 inline-flex items-center gap-1 ml-auto"
                      data-testid={`catalog-delete-${p.id}`}
                    >
                      <Trash2 size={13} /> {t.catalog.deleteBtn}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Empty placeholder slots (clickable) */}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <button
              type="button"
              key={`slot-${i}`}
              onClick={openNew}
              className="rounded-2xl border-2 border-dashed border-gray-300 bg-cream/40 hover:border-orange hover:bg-orange/5 transition-colors flex flex-col items-center justify-center text-teal-soft hover:text-orange aspect-square min-h-[260px]"
              data-testid={`catalog-empty-slot-${i}`}
            >
              <div className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center">
                <Plus size={20} />
              </div>
              <span className="text-sm font-semibold mt-3">{t.catalog.addSlotLabel}</span>
            </button>
          ))}
        </div>

        {/* Slots counter (single source of truth, bottom) */}
        <div className="mt-4 text-xs text-teal-soft text-right" data-testid="catalog-slots">
          {t.catalog.slotsLeft.replace("{n}", items.length).replace("{limit}", limit)}
        </div>

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={closeModal} data-testid="catalog-modal">
          <div
            className="bg-white w-full md:max-w-lg rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative p-6 border-b border-gray-100">
              <button
                type="button"
                onClick={closeModal}
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-cream hover:bg-gray-200 flex items-center justify-center"
                data-testid="catalog-modal-close"
              >
                <X size={16} />
              </button>
              <p className="eyebrow text-orange">{t.catalog.title}</p>
              <h3 className="font-display text-2xl text-teal-deep mt-1 leading-tight pr-8">
                {editing.mode === "new" ? t.catalog.modalNewTitle : t.catalog.modalEditTitle}
              </h3>
            </div>

            <form onSubmit={save} className="p-6 space-y-4 overflow-y-auto" data-testid="catalog-form">
              {/* Type toggle: Product vs Service */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-teal block mb-2">{t.catalog.fType}</label>
                <div className="inline-flex bg-cream rounded-full p-1 border border-gray-200">
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, type: "product" })}
                    className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${editing.type === "product" ? "bg-orange text-white shadow" : "text-teal-soft hover:text-teal"}`}
                    data-testid="catalog-type-product"
                  >
                    <Package size={12} /> {t.catalog.productLabel}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, type: "service" })}
                    className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${editing.type === "service" ? "bg-teal text-white shadow" : "text-teal-soft hover:text-teal"}`}
                    data-testid="catalog-type-service"
                  >
                    <Wrench size={12} /> {t.catalog.serviceLabel}
                  </button>
                </div>
              </div>

              <ImageUpload
                label={t.catalog.fImage}
                hint={t.catalog.fImageHint}
                value={editing.image}
                onChange={(v) => setEditing({ ...editing, image: v })}
                aspect="square"
                testid="catalog-image-upload"
              />

              <GalleryUpload
                label={t.catalog.fGallery}
                hint={t.catalog.fGalleryHint}
                value={Array.isArray(editing.gallery) ? editing.gallery : []}
                onChange={(arr) => setEditing((prev) => ({ ...prev, gallery: arr }))}
                max={6}
                testid="catalog-gallery-upload"
              />

              <Field label={t.catalog.fTitle}>
                <input
                  required
                  maxLength={120}
                  className="field-input"
                  placeholder={t.catalog.fTitlePh}
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  data-testid="catalog-field-title"
                />
              </Field>

              <Field label={t.catalog.fDescription}>
                <textarea
                  rows={2}
                  maxLength={500}
                  className="field-input"
                  placeholder={t.catalog.fDescriptionPh}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  data-testid="catalog-field-desc"
                />
              </Field>

              <Field label={t.catalog.fCategory}>
                <input
                  maxLength={80}
                  className="field-input"
                  placeholder={t.catalog.fCategoryPh}
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  data-testid="catalog-field-category"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label={t.catalog.fPrice}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="field-input"
                    placeholder={t.catalog.fPricePh}
                    value={editing.price}
                    onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                    data-testid="catalog-field-price"
                  />
                </Field>
                <Field label={t.catalog.fCurrency}>
                  <select
                    className="field-input"
                    value={editing.currency}
                    onChange={(e) => setEditing({ ...editing, currency: e.target.value })}
                    data-testid="catalog-field-currency"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </Field>
              </div>

              {editing.type === "service" && (
                <Field label={t.catalog.fDuration}>
                  <input
                    maxLength={40}
                    className="field-input"
                    placeholder={t.catalog.fDurationPh}
                    value={editing.duration}
                    onChange={(e) => setEditing({ ...editing, duration: e.target.value })}
                    data-testid="catalog-field-duration"
                  />
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {DURATION_CHIPS.map((c) => (
                      <button
                        type="button"
                        key={c}
                        onClick={() => setEditing({ ...editing, duration: c })}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${editing.duration === c ? "bg-teal text-white border-teal" : "bg-white text-teal-soft border-gray-200 hover:border-teal"}`}
                        data-testid={`duration-chip-${c.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </Field>
              )}

              {error && <p className="text-red-600 text-sm">{error}</p>}

              <div className="pt-2 flex items-center gap-3">
                <button type="submit" disabled={saving} className="btn-orange" data-testid="catalog-save-btn">
                  {saving ? "…" : t.catalog.saveBtn}
                </button>
                <button type="button" onClick={closeModal} className="text-sm text-teal-soft hover:text-teal">
                  {t.catalog.cancelBtn}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-wider text-teal block mb-1">{label}</label>
    {children}
  </div>
);

export default ProductCatalogManager;
